import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import { Button } from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import StoreIcon from "@mui/icons-material/Store";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import Groups3Icon from "@mui/icons-material/Groups3";
import ComputerIcon from "@mui/icons-material/Computer";
import HandymanIcon from "@mui/icons-material/Handyman";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CalculateIcon from "@mui/icons-material/Calculate";
import AdbIcon from "@mui/icons-material/Adb";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import HomeIcon from "@mui/icons-material/Home";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { MENU_WIDTH } from "../../types/constants";
import { setShowCashierInfosModal } from "../../store/actions/cashierSlice";
import { setShowOnboardingModal } from "../../store/actions/onboardingSlice";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import { canManage, isOwner } from "../../common/validators";
import TableBarIcon from "@mui/icons-material/TableBar";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { ConfirmationNumber } from "@mui/icons-material";
import HelpIcon from "@mui/icons-material/Help";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import SchoolIcon from "@mui/icons-material/School";
import TutorialTooltip from "../common/TutorialTooltip";
import {
  hasTooltipBeenShown,
  markTooltipAsShown,
} from "../../utils/tooltipTutorial";

// ─── Product accent colors ───
const ACCENT_COST = "#059669";
const ACCENT_LABEL = "#d97706";
const ACCENT_STOCK = "#7c3aed";
const ACCENT_EMPLOYEE = "#2563eb";
const ACCENT_AI = "#4f46e5";

// ─── Styled components ───
const FloatingArrow = styled(Stack)(({ theme, expanded }) => ({
  position: "fixed",
  left: 0,
  top: "50%",
  transform: "translateY(-50%)",
  width: expanded ? 0 : "44px",
  height: "132px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  backgroundColor: "#fff",
  borderRadius: "0 6px 6px 0",
  boxShadow: "2px 0 16px rgba(15, 23, 42, 0.08)",
  cursor: "pointer",
  zIndex: theme.zIndex.appBar + 1,
  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  opacity: expanded ? 0 : 1,
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: "none",
}));

const SideMenuContainer = styled(Box)(({ theme, expanded }) => ({
  position: "fixed",
  left: expanded ? 0 : `-${MENU_WIDTH}`,
  top: 0,
  height: "100vh",
  width: MENU_WIDTH,
  backgroundColor: "#fff",
  transition: "left 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: theme.zIndex.appBar,
  display: "flex",
  flexDirection: "column",
  boxShadow: expanded ? "4px 0 24px rgba(15, 23, 42, 0.08)" : "none",
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: "20px 16px 14px",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: 4,
}));

const ScrollableMenuContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflowY: "auto",
  overflowX: "hidden",
  paddingRight: 4,
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.grey[300]} transparent`,
  "&::-webkit-scrollbar": {
    width: "3px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[300],
    borderRadius: "3px",
    "&:hover": {
      backgroundColor: theme.palette.grey[400],
    },
  },
}));

const FixedBottomSection = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  marginBottom: 8,
  flexShrink: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: 8,
}));

const MenuButton = styled(Button)(({ theme, active }) => ({
  width: `calc(100% - ${theme.spacing(2)})`,
  justifyContent: "flex-start",
  padding: theme.spacing(1.2, 1.5),
  margin: theme.spacing(0.25, 1),
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.06)
    : "transparent",
  borderRadius: 6,
  position: "relative",
  transition: "all 0.2s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: active ? 3 : 0,
    height: active ? "55%" : 0,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "0 3px 3px 0",
    transition: "all 0.2s ease",
  },
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
  },
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1.5),
    fontSize: 20,
    color: active ? theme.palette.primary.main : theme.palette.grey[400],
  },
  textTransform: "none",
}));

// ─── Simples section ───
const SimplesSection = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1, 1),
  padding: theme.spacing(1.5, 0, 1),
  borderRadius: 6,
  background: `linear-gradient(160deg, ${alpha(ACCENT_COST, 0.06)} 0%, ${alpha(
    ACCENT_LABEL,
    0.05
  )} 50%, ${alpha(ACCENT_EMPLOYEE, 0.06)} 100%)`,
  border: `1px solid ${theme.palette.grey[200]}`,
  position: "relative",
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${ACCENT_COST}, ${ACCENT_LABEL}, ${ACCENT_EMPLOYEE})`,
    borderRadius: "6px 6px 0 0",
  },
}));

const SimplesSectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const SimplesChip = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 16px",
  borderRadius: 4,
  background: `linear-gradient(135deg, ${alpha(ACCENT_COST, 0.08)} 0%, ${alpha(
    ACCENT_LABEL,
    0.06
  )} 50%, ${alpha(ACCENT_EMPLOYEE, 0.08)} 100%)`,
  border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
  cursor: "pointer",
  transition: "all 0.25s ease",
  textDecoration: "none",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      ACCENT_COST,
      0.12
    )} 0%, ${alpha(ACCENT_LABEL, 0.1)} 50%, ${alpha(
      ACCENT_EMPLOYEE,
      0.12
    )} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(ACCENT_LABEL, 0.15)}`,
  },
}));

const SimplesMenuButton = styled(Button)(({ theme, active, accentcolor }) => ({
  width: `calc(100% - ${theme.spacing(2)})`,
  justifyContent: "flex-start",
  padding: theme.spacing(1.2, 1.5),
  margin: theme.spacing(0.3, 1),
  color: active ? accentcolor : theme.palette.text.secondary,
  backgroundColor: active ? alpha(accentcolor, 0.06) : "transparent",
  borderRadius: 6,
  position: "relative",
  transition: "all 0.25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: active ? 3 : 0,
    height: active ? "55%" : 0,
    backgroundColor: accentcolor,
    borderRadius: "0 3px 3px 0",
    transition: "all 0.25s ease",
  },
  "&:hover": {
    backgroundColor: alpha(accentcolor, 0.06),
    color: accentcolor,
    transform: "translateX(2px)",
    "& .simples-icon": {
      color: accentcolor,
    },
  },
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1.5),
    fontSize: 20,
    transition: "color 0.25s ease",
  },
  textTransform: "none",
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.6rem",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: theme.palette.grey[400],
  padding: "14px 20px 6px",
}));

const MenuWrapper = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  zIndex: 1200,
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
});

export default function DesktopMenu({ handleRestaurants, logout }) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [showArrowTooltip, setShowArrowTooltip] = useState(false);
  const [showTutorialTooltip, setShowTutorialTooltip] = useState(false);
  const arrowRef = useRef(null);
  const tutorialRef = useRef(null);

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.auth.user);
  const userType = useSelector((state) => state.auth.user?.user_type);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleShowCashierInfosModal = () => {
    dispatch(setShowCashierInfosModal(true));
  };

  const handleShowOnboarding = () => {
    dispatch(setShowOnboardingModal(true));
    setExpanded(false);
  };

  const handleCloseArrowTooltip = () => {
    markTooltipAsShown("FLOATING_ARROW");
    setShowArrowTooltip(false);
  };

  const handleCloseTutorialTooltip = () => {
    markTooltipAsShown("FLOATING_TUTORIAL");
    setShowTutorialTooltip(false);

    if (!hasTooltipBeenShown("FLOATING_ARROW")) {
      setTimeout(() => {
        setShowArrowTooltip(true);
      }, 500);
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  useEffect(() => {
    const checkTooltips = () => {
      if (!hasTooltipBeenShown("FLOATING_TUTORIAL")) {
        setShowTutorialTooltip(true);
      } else if (!hasTooltipBeenShown("FLOATING_ARROW")) {
        setShowArrowTooltip(true);
      }
    };

    const timer = setTimeout(checkTooltips, 1000);
    return () => clearTimeout(timer);
  }, []);

  const renderFloatingArrow = () => {
    const shouldShowHomeIcon = !location.pathname.startsWith("/central-");

    return (
      <>
        <FloatingArrow spacing={0.5} expanded={expanded}>
          <Box
            ref={arrowRef}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.08),
              borderRadius: "4px",
              padding: "6px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.15),
              },
            }}
            onMouseEnter={() => {
              setExpanded(true);
              if (showArrowTooltip) {
                handleCloseArrowTooltip();
              }
            }}
          >
            <ArrowForwardIosIcon
              sx={{
                fontSize: 16,
                color: "primary.main",
              }}
            />
          </Box>
          {shouldShowHomeIcon && (
            <Tooltip title={"Central"} placement="right-start">
              <Box
                sx={{
                  padding: "6px",
                  display: "flex",
                  borderRadius: "4px",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.08),
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.15),
                  },
                }}
              >
                <HomeIcon
                  sx={{
                    fontSize: 18,
                    color: "primary.main",
                  }}
                  onClick={() => history.push("/")}
                />
              </Box>
            </Tooltip>
          )}
          <Tooltip title={"Tutorial"} placement="right-start">
            <Box
              ref={tutorialRef}
              sx={{
                padding: "6px",
                display: "flex",
                borderRadius: "4px",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(ACCENT_LABEL, 0.1),
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(ACCENT_LABEL, 0.18),
                },
              }}
              onClick={() => {
                handleShowOnboarding();
                if (showTutorialTooltip) {
                  handleCloseTutorialTooltip();
                }
              }}
            >
              <SchoolIcon
                sx={{
                  fontSize: 18,
                  color: ACCENT_LABEL,
                }}
              />
            </Box>
          </Tooltip>
        </FloatingArrow>

        {/* Tutorial Tooltips */}
        <TutorialTooltip
          open={showArrowTooltip && !expanded}
          anchorEl={arrowRef.current}
          message="Passe o mouse aqui para abrir o menu"
          emoji="👉"
          onClose={handleCloseArrowTooltip}
          color="primary"
          placement="right"
        />

        <TutorialTooltip
          open={showTutorialTooltip && !expanded}
          anchorEl={tutorialRef.current}
          message="Clique aqui para aprender mais sobre o Slapie"
          emoji="🎓"
          onClose={handleCloseTutorialTooltip}
          color="secondary"
          placement="right"
        />
      </>
    );
  };


  const renderSimplesSection = () => {
    const canUserManage = canManage(userType);

    if (!canUserManage) {
      return null;
    }

    const isCostActive = isActiveRoute("/controle-custos");
    const isStockActive = isActiveRoute("/estoque-simples");
    const isLabelActive = isActiveRoute("/etiqueta-simples");
    const isEmployeeActive = isActiveRoute("/funcionario-simples");

    return (
      <SimplesSection>
        <SimplesSectionHeader>
          <SimplesChip component={RouterLink} to="/slapie-simples">
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "1px",
                background: `linear-gradient(90deg, ${ACCENT_COST}, ${ACCENT_LABEL}, ${ACCENT_EMPLOYEE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              SLAPIE SIMPLES
            </Typography>
          </SimplesChip>
        </SimplesSectionHeader>

        <SimplesMenuButton
          component={RouterLink}
          to="/controle-custos"
          active={isCostActive}
          accentcolor={ACCENT_COST}
        >
          <CalculateIcon
            className="simples-icon"
            sx={{ color: isCostActive ? ACCENT_COST : grey[400] }}
          />
          <Typography
            sx={{
              color: "inherit",
              fontSize: "0.85rem",
              fontWeight: isCostActive ? 600 : 400,
            }}
          >
            Custo Simples
          </Typography>
        </SimplesMenuButton>

        <SimplesMenuButton
          component={RouterLink}
          to="/estoque-simples"
          active={isStockActive}
          accentcolor={ACCENT_STOCK}
        >
          <Inventory2Icon
            className="simples-icon"
            sx={{ color: isStockActive ? ACCENT_STOCK : grey[400] }}
          />
          <Typography
            sx={{
              color: "inherit",
              fontSize: "0.85rem",
              fontWeight: isStockActive ? 600 : 400,
            }}
          >
            Estoque Simples
          </Typography>
        </SimplesMenuButton>

        <SimplesMenuButton
          component={RouterLink}
          to="/etiqueta-simples"
          active={isLabelActive}
          accentcolor={ACCENT_LABEL}
        >
          <ConfirmationNumber
            className="simples-icon"
            sx={{ color: isLabelActive ? ACCENT_LABEL : grey[400] }}
          />
          <Typography
            sx={{
              color: "inherit",
              fontSize: "0.85rem",
              fontWeight: isLabelActive ? 600 : 400,
            }}
          >
            Etiqueta Simples
          </Typography>
        </SimplesMenuButton>

        <SimplesMenuButton
          component={RouterLink}
          to="/funcionario-simples"
          active={isEmployeeActive}
          accentcolor={ACCENT_EMPLOYEE}
        >
          <SmartToyIcon
            className="simples-icon"
            sx={{ color: isEmployeeActive ? ACCENT_EMPLOYEE : grey[400] }}
          />
          <Typography
            sx={{
              color: "inherit",
              fontSize: "0.85rem",
              fontWeight: isEmployeeActive ? 600 : 400,
            }}
          >
            Assistente Simples
          </Typography>
        </SimplesMenuButton>
      </SimplesSection>
    );
  };

  const renderMainMenuItems = () => {
    const canUserManage = canManage(userType);
    const isDeliveryActive = isActiveRoute("/central-delivery");
    const isSaloonActive = isActiveRoute("/central-salao");
    const isPackingActive = isActiveRoute("/central-embalagem");
    const isKitchenActive = isActiveRoute("/cozinha");
    const isCustomersActive = isActiveRoute("/customers");
    const isProductsActive = isActiveRoute("/products");
    const isOrderHistoryActive = isActiveRoute("/historico/pedidos");
    const isDeliveryHistoryActive = isActiveRoute("/historico/entregas");
    const isCashierHistoryActive = isActiveRoute("/historico/caixa");
    const isFiscalHistoryActive = isActiveRoute("/historico/fiscal");
    const isReportsActive = isActiveRoute("/reports");
    const isDeliveryCenterActive = isActiveRoute("/delivery-center");
    const isAssistantActive = isActiveRoute("/assistente-ia");

    return (
      <>
        <SimplesMenuButton
          component={RouterLink}
          to="/assistente-ia"
          active={isAssistantActive}
          accentcolor={ACCENT_AI}
        >
          <AutoAwesomeIcon
            className="simples-icon"
            sx={{ color: isAssistantActive ? ACCENT_AI : grey[400] }}
          />
          <Typography
            sx={{
              color: "inherit",
              fontSize: "0.85rem",
              fontWeight: isAssistantActive ? 600 : 400,
            }}
          >
            Slapie IA
          </Typography>
        </SimplesMenuButton>

        {renderSimplesSection()}

        <SectionLabel>Operacao</SectionLabel>

        <MenuButton
          component={RouterLink}
          to="/central-delivery"
          active={isDeliveryActive}
        >
          <ComputerIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Central Delivery
          </Typography>
        </MenuButton>

        <MenuButton
          component={RouterLink}
          to="/central-salao"
          active={isSaloonActive}
        >
          <TableBarIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Central Salao
          </Typography>
        </MenuButton>

        <MenuButton
          component={RouterLink}
          to="/central-embalagem"
          active={isPackingActive}
        >
          <BabyChangingStationIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Central Embalagem
          </Typography>
        </MenuButton>

        <MenuButton
          component={RouterLink}
          to="/cozinha"
          active={isKitchenActive}
        >
          <SoupKitchenIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Cozinha
          </Typography>
        </MenuButton>

        {canUserManage && (
          <MenuButton onClick={handleRestaurants} active={false}>
            <StoreIcon />
            <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
              Lojas
            </Typography>
          </MenuButton>
        )}

        <MenuButton
          component={RouterLink}
          to="/customers"
          active={isCustomersActive}
        >
          <Groups3Icon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Clientes
          </Typography>
        </MenuButton>

        <MenuButton
          component={RouterLink}
          to="/products"
          active={isProductsActive}
        >
          <MenuBookIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Cardapios
          </Typography>
        </MenuButton>

        <SectionLabel>Historico</SectionLabel>

        <MenuButton
          component={RouterLink}
          to="/historico/pedidos"
          active={isOrderHistoryActive}
        >
          <ListAltIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Pedidos
          </Typography>
        </MenuButton>

        <MenuButton
          component={RouterLink}
          to="/historico/entregas"
          active={isDeliveryHistoryActive}
        >
          <DeliveryDiningIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Entregas
          </Typography>
        </MenuButton>

        {canUserManage && (
          <MenuButton
            component={RouterLink}
            to="/historico/caixa"
            active={isCashierHistoryActive}
          >
            <AccountBalanceWalletIcon />
            <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
              Caixas
            </Typography>
          </MenuButton>
        )}

        {canUserManage && (
          <MenuButton
            component={RouterLink}
            to="/historico/fiscal"
            active={isFiscalHistoryActive}
          >
            <ConfirmationNumber />
            <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
              NFe
            </Typography>
          </MenuButton>
        )}

        {canUserManage && (
          <>
            <SectionLabel>Administrador</SectionLabel>

            <MenuButton
              component={RouterLink}
              to="/reports"
              active={isReportsActive}
            >
              <HistoryIcon />
              <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
                Relatorios
              </Typography>
            </MenuButton>
          </>
        )}

        {canUserManage && (
          <MenuButton
            component={RouterLink}
            to="/delivery-center"
            active={isDeliveryCenterActive}
          >
            <HandymanIcon />
            <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
              Configuracoes Gerais
            </Typography>
          </MenuButton>
        )}
      </>
    );
  };

  const renderBottomSection = () => {
    const shouldShowCashier = !isActiveRoute("/central-embalagem");

    return (
      <FixedBottomSection>
        <MenuButton onClick={() => history.push("/ajuda/")}>
          <HelpIcon />
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            Ajuda
          </Typography>
        </MenuButton>

        {shouldShowCashier && (
          <MenuButton onClick={handleShowCashierInfosModal}>
            <PointOfSaleIcon />
            <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
              Meu Caixa
            </Typography>
          </MenuButton>
        )}
        <MenuButton onClick={handleOpenUserMenu}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
              flexShrink: 0,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#fff",
            }}
          >
            {user.username?.charAt(0)?.toUpperCase() || "U"}
          </Box>
          <Typography sx={{ color: "inherit", fontSize: "0.85rem" }}>
            {user.username}
          </Typography>
        </MenuButton>
      </FixedBottomSection>
    );
  };

  const renderUserMenu = () => {
    const isMenuOpen = Boolean(anchorElUser);

    const handleChangePassword = () => {
      handleCloseUserMenu();
    };

    const handleLogout = () => {
      handleCloseUserMenu();
      logout();
    };

    return (
      <Menu
        anchorEl={anchorElUser}
        open={isMenuOpen}
        onClose={handleCloseUserMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          component={RouterLink}
          to="/change-password"
          onClick={handleChangePassword}
        >
          <KeyIcon sx={{ mr: 1 }} />
          <Typography>Mudar Senha</Typography>
        </MenuItem>
        {isOwner(userType) && (
          <MenuItem
            component={RouterLink}
            to="/minha-assinatura"
            onClick={handleCloseUserMenu}
          >
            <CardMembershipIcon sx={{ mr: 1 }} />
            <Typography>Minha Assinatura</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          <Typography>Sair</Typography>
        </MenuItem>
      </Menu>
    );
  };

  const isUserValid = user && isLoggedIn;

  if (!isUserValid) {
    return null;
  }

  return (
    <MenuWrapper>
      {renderFloatingArrow()}

      <SideMenuContainer
        expanded={expanded}
        onMouseLeave={() => setExpanded(false)}
      >
        <MenuHeader>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "4px",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
            }}
          >
            <AdbIcon sx={{ fontSize: 16, color: "#fff" }} />
          </Box>
          <Typography
            variant="h6"
            noWrap
            sx={{
              color: "text.primary",
              fontWeight: 700,
              fontSize: "1.05rem",
              letterSpacing: "-0.02em",
            }}
          >
            Slapie
          </Typography>
        </MenuHeader>

        <ScrollableMenuContent>{renderMainMenuItems()}</ScrollableMenuContent>

        {renderBottomSection()}

        {renderUserMenu()}
      </SideMenuContainer>
    </MenuWrapper>
  );
}
